/*
  ============================================================================
  bb-install-guide.js  —  BlackBoost installation guide connector
  ----------------------------------------------------------------------------
  Owner:    Linea Group (lineagroup.ae) for BlackBoost USA
  Store:    blackboost-usa.myshopify.com  —  theme "Linea DEV - do not publish"
  Created:  2026-08-12

  WHAT THIS IS
  The store publishes 35 installation PDFs on /pages/installation and no product
  page links to any of them. This file takes the SKU that Liquid already printed
  into the markup, looks it up in assets/bb-install-guides.json, and renders one
  link. On a miss it renders nothing at all.

  THE MATCH IS EXACT, ON PURPOSE
  Keys are the exact Shopify variant SKU, uppercased and trimmed. Nothing else
  is normalised. Separators are NOT stripped: BBCAIS027 (Golf MK7), BBCAIS027-1
  (MK8 GTI) and BBCAIS027-2 (MK8 R) are three different airboxes and collapsing
  the separator would cross-link them. Prefixes are NOT matched either:
  BBCAIS007 is the BMW M5 F90 and BBCAIS007.1 is the 850i.
  A miss is the correct, safe outcome. Linking the wrong guide to a $2,000 part
  is worse than linking none, and the shopper still has /pages/installation.

  RENDER WITHOUT WRITING LOGIC
  Put the snippet on the page and this file fills it in:
    {% render 'bb-install-guide', product: product %}
  which emits <div data-bb-guide ...> with an empty [data-bb-guide-slot] inside.
  Only that slot is written to, so the <noscript> fallback beside it survives.

  The host also gets data-bb-guide-state="hit" | "miss" | "idle" so CSS or
  another component can react without any extra JS.

  VARIANTS
  Dawn does not re-render our markup on a variant change: product-info.js
  surgically replaces price, SKU, inventory and a few other blocks, then
  publishes 'variant-change' on its own pub/sub. So we track variants two ways,
  neither of which reaches into Dawn's internals as a hard dependency:
    1. the bubbling 'change' event Dawn fires on input[name="id"], and
    2. window.subscribe(PUB_SUB_EVENTS.variantChange) when it happens to exist.
  The variant id -> SKU map is printed by Liquid, so no network call is needed
  to answer "which SKU is selected now".

  NO JS
  Nothing here is required to read or buy the product. With JS off the slot
  stays empty and the snippet's <noscript> shows a link to the guide index page.

  SEO
  This file adds an outbound link to the store's own CDN. It does not touch
  <link rel="canonical">, the URL, or product.description.

  Dependencies: none. No jQuery, no CDN, no build step.
  ============================================================================
*/

(function () {
  'use strict';

  if (window.BBInstallGuides && window.BBInstallGuides.__isBBInstallGuides) return;

  /* ------------------------------------------------------------------ config */

  var VERSION = '1.0.0';
  var HOST_SELECTOR = '[data-bb-guide]:not([data-bb-guide-manual])';

  var scriptEl = document.currentScript || document.querySelector('script[data-bb-guides-url]');
  var settings = window.BBInstallGuidesConfig || {};

  function attr(name, fallback) {
    if (scriptEl && scriptEl.getAttribute(name)) return scriptEl.getAttribute(name);
    return fallback;
  }

  var config = {
    dataUrl: settings.dataUrl || attr('data-bb-guides-url', ''),
    indexUrl: settings.indexUrl || attr('data-bb-guides-index-url', '/pages/installation')
  };

  // Every string the shopper reads. Override with window.BBInstallGuidesConfig.copy
  // before this file loads if the client wants different wording.
  var COPY = {
    link: 'Installation guide (PDF)',
    newTab: ', opens in a new tab',
    allGuides: 'All installation guides'
  };
  if (settings.copy) {
    for (var ck in settings.copy) {
      if (Object.prototype.hasOwnProperty.call(settings.copy, ck)) COPY[ck] = settings.copy[ck];
    }
  }

  /* ------------------------------------------------------------ small helpers */

  function noop() {}

  function isEl(node) {
    return node && node.nodeType === 1;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function empty(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function normalizeSku(value) {
    if (value == null) return '';
    return String(value).trim().toUpperCase();
  }

  // Own properties only. A SKU is merchant-typed data and "constructor" or
  // "__proto__" would otherwise resolve to something off Object.prototype.
  function own(object, key) {
    return object && Object.prototype.hasOwnProperty.call(object, key) ? object[key] : null;
  }

  // Only ever emit http(s) links. The dataset is ours, but a bad row must not
  // become a javascript: or data: URL on a live product page.
  function safeUrl(value) {
    if (!value) return '';
    var url = String(value).trim();
    return /^https?:\/\//i.test(url) || url.charAt(0) === '/' ? url : '';
  }

  /* ------------------------------------------------------------------ dataset */

  var guides = null;
  var loadPromise = null;

  function load() {
    if (loadPromise) return loadPromise;

    if (!config.dataUrl) {
      loadPromise = Promise.reject(new Error('bb-install-guide: no dataset URL configured'));
      loadPromise.catch(noop);
      return loadPromise;
    }

    loadPromise = fetch(config.dataUrl, { credentials: 'omit' })
      .then(function (response) {
        if (!response.ok) throw new Error('bb-install-guide: dataset HTTP ' + response.status);
        return response.json();
      })
      .then(function (json) {
        guides = json && typeof json === 'object' ? json : {};
        renderAll();
        return guides;
      });

    // Dataset down: every host stays 'idle' and renders nothing. Safe direction.
    loadPromise.catch(noop);

    return loadPromise;
  }

  function lookup(sku) {
    var key = normalizeSku(sku);
    if (!key || !guides) return null;
    var row = own(guides, key);
    if (!row || typeof row !== 'object') return null;
    var url = safeUrl(row.url);
    if (!url) return null;
    return { sku: key, url: url, title: row.title ? String(row.title) : '' };
  }

  /* ------------------------------------------------------------------- render */

  function parseMap(host) {
    var raw = host.getAttribute('data-bb-guide-variants');
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  // The SKU currently on screen: whatever Liquid printed, unless a variant has
  // been chosen since and the map knows a different SKU for it.
  function currentSku(host) {
    var chosen = host.getAttribute('data-bb-guide-variant');
    if (chosen) {
      var map = parseMap(host);
      var fromMap = map ? own(map, String(chosen)) : null;
      if (fromMap != null) return normalizeSku(fromMap);
      // Variant id we have no row for. Do not fall back to the first variant's
      // SKU: that is how a two-variant product gets the wrong guide.
      return '';
    }
    return normalizeSku(host.getAttribute('data-bb-guide-sku'));
  }

  function docIcon() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', 'bb-guide__icon');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute(
      'd',
      'M8.5 1v3.5H12L8.5 1zM3 0h6l4 4v11a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V1a1 1 0 0 1 1-1zm1.5 8h7v1h-7V8zm0 2.5h7v1h-7v-1zm0-5h2.5v1H4.5v-1z'
    );
    svg.appendChild(path);
    return svg;
  }

  function renderHost(host) {
    var slot = host.querySelector('[data-bb-guide-slot]');
    if (!slot) return;

    if (!guides) {
      host.setAttribute('data-bb-guide-state', 'idle');
      empty(slot);
      return;
    }

    var match = lookup(currentSku(host));

    if (!match) {
      // No guide for this SKU. Render nothing at all, not an empty state and
      // not a "guide coming soon". 102 of 132 products land here.
      host.setAttribute('data-bb-guide-state', 'miss');
      empty(slot);
      return;
    }

    host.setAttribute('data-bb-guide-state', 'hit');

    var block = el('div', 'bb-guide__block');

    var headingText = host.getAttribute('data-bb-guide-heading');
    if (headingText) block.appendChild(el('p', 'bb-guide__heading', headingText));

    var link = el('a', 'bb-guide__link bb-btn bb-btn--ghost');
    link.href = match.url;
    link.target = '_blank';
    link.rel = 'noopener';
    link.appendChild(docIcon());
    link.appendChild(el('span', 'bb-guide__label', COPY.link));
    link.appendChild(el('span', 'bb-visually-hidden', COPY.newTab));
    block.appendChild(link);

    // The guide's own title, verbatim from the client's page. It is here so a
    // shopper can see which document they are about to open and tell us if it
    // is not their car, rather than us silently asserting a match.
    if (match.title && host.getAttribute('data-bb-guide-show-title') !== 'false') {
      block.appendChild(el('p', 'bb-guide__meta', match.title));
    }

    var note = host.getAttribute('data-bb-guide-note');
    if (note) block.appendChild(el('p', 'bb-guide__note', note));

    empty(slot);
    slot.appendChild(block);
  }

  function renderAll(root) {
    var scope = root || document;
    var hosts = scope.querySelectorAll(HOST_SELECTOR);
    for (var i = 0; i < hosts.length; i++) renderHost(hosts[i]);
    if (isEl(scope) && scope.matches && scope.matches(HOST_SELECTOR)) renderHost(scope);
  }

  /* ------------------------------------------------------- variant tracking */

  // Scoped by ownership, not by DOM position: a variant id belongs to exactly
  // one product, so a host reacts only if its own map knows that id. This is
  // what makes the snippet safe to place in a different section from the
  // variant picker, which is where it lands on this store (the PDP is
  // main-product + a custom-liquid section + related-products).
  function setVariant(variantId) {
    if (!variantId) return;
    var id = String(variantId);
    var hosts = document.querySelectorAll(HOST_SELECTOR);
    for (var i = 0; i < hosts.length; i++) {
      var map = parseMap(hosts[i]);
      if (!map || own(map, id) === null) continue;
      hosts[i].setAttribute('data-bb-guide-variant', id);
      renderHost(hosts[i]);
    }
  }

  // Dawn's updateVariantInputs() sets input[name="id"].value and dispatches a
  // bubbling 'change' on it. That is a plain DOM event, so this keeps working
  // even if Dawn's pub/sub is renamed or dropped in a future version.
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!isEl(target) || target.name !== 'id') return;
    if (!target.form && !(target.closest && target.closest('form'))) return;
    setVariant(target.value);
  });

  // Belt and braces: Dawn's own event, when it is there.
  function subscribeToDawn() {
    if (typeof window.subscribe !== 'function') return;
    var events = window.PUB_SUB_EVENTS;
    if (!events || !events.variantChange) return;
    try {
      window.subscribe(events.variantChange, function (payload) {
        var data = payload && payload.data;
        if (data && data.variant) setVariant(data.variant.id);
      });
    } catch (e) {
      /* ignore */
    }
  }

  /* ---------------------------------------------------------- DOM watching */

  // Quick-add drawers and section re-renders inject markup after load.
  function watchDom() {
    if (!window.MutationObserver) return;
    var queued = [];
    var scheduled = false;

    function flush() {
      scheduled = false;
      var nodes = queued;
      queued = [];
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].isConnected) renderAll(nodes[i]);
      }
    }

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (!isEl(added[j])) continue;
          if (
            (added[j].matches && added[j].matches(HOST_SELECTOR)) ||
            (added[j].querySelector && added[j].querySelector(HOST_SELECTOR))
          ) {
            queued.push(added[j]);
          }
        }
      }
      if (queued.length && !scheduled) {
        scheduled = true;
        requestAnimationFrame(flush);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* --------------------------------------------------------------- public API */

  var api = {
    __isBBInstallGuides: true,
    VERSION: VERSION,
    config: config,
    copy: COPY,

    // -> { sku, url, title } | null. Exact match, nothing clever.
    find: function (sku) {
      return lookup(sku);
    },
    ready: function () {
      return load();
    },
    refresh: renderAll,
    count: function () {
      return guides ? Object.keys(guides).length : 0;
    }
  };

  Object.defineProperty(api, 'data', {
    get: function () {
      return guides;
    },
    enumerable: true
  });

  window.BBInstallGuides = api;

  /* ---------------------------------------------------------------- bootstrap */

  function boot() {
    document.documentElement.classList.add('bb-guide-ready');
    subscribeToDawn();
    watchDom();
    renderAll();
    load().then(noop, noop);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
